import React, { useState } from "react";
import { triggerConfetti } from 'src/hooks/useConfetti';
import { ReactComponent as Instagram } from 'src/assets/sns/InstagramAuthor.svg';
import { ReactComponent as TwitterX } from 'src/assets/sns/XAuthor.svg';
import { ReactComponent as Snslink } from 'src/assets/sns/snslink.svg';
import SnsModal from "../../../common/layouts/modal/SnsModal";
import Alert from "../../../common/layouts/modal/Alert";
import { ReactComponent as Info } from 'src/assets/info.svg';
import {ReactComponent as Twitter} from 'src/assets/author/x_author.svg';
import {ReactComponent as Instar} from 'src/assets/author/instar_author.svg';
import {ReactComponent as Link} from 'src/assets/author/link_author.svg';
import {ReactComponent as Follow} from 'src/assets/author/follow_author.svg';
import {ReactComponent as Book} from 'src/assets/author/book_author.svg';
import { motion } from "framer-motion";

interface AuthorDetailPageProps {
    authors: any;
    isFollowing: boolean;
    onFollowToggle: () => void;
    followAlert: boolean;
    setFollowAlert: (value: boolean) => void;
    bookCount?: number;
    bookLikeCount?: number;
}

const AuthorDetailPage = ({ authors, isFollowing, onFollowToggle, followAlert, setFollowAlert, bookCount, bookLikeCount }: AuthorDetailPageProps) => {
    if (!authors) return null;

    const testYn = authors.testStatus;

    const authorStatus: string | null = localStorage.getItem("authorStatus");
    const authorEmail: string | null = localStorage.getItem("email");
    const authorYn: boolean = !!authorStatus && authorEmail === authors.authorEmail;

    const [showAlert, setShowAlert] = useState(false);

    const profileImageSrc = authors.profilePhoto
        ? `data:image/png;base64,${authors.profilePhoto}`
        : `${process.env.PUBLIC_URL}/img/profile_sam.jpg`;

    const genres: string[] = authors.writingGenre
        ? authors.writingGenre.split(',').map((g: string) => g.trim()).filter(Boolean)
        : [];

    return (
        <div className="flex flex-col gap-3 w-[300px] flex-shrink-0">
            {testYn && (
                <div className="flex flex-row gap-1.5 items-center text-[#727272] text-xs font-normal px-2 mb-2">
                    <Info className="w-4 h-4 flex-shrink-0" />
                    <span>이 페이지는 예시 데이터로 구성되었습니다.</span>
                </div>
            )}

            {/* Profile card */}
            <div className="bg-white rounded-3xl border border-[#e5e7eb] shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_0px_rgba(0,0,0,0.1)] overflow-hidden">
                {/* Card top: gradient background */}

                {/* Profile photo overlapping */}
                <div className="flex flex-col items-center px-6 py-8">
                    <div className="relative w-[127px] h-[127px] flex-shrink-0">
                        <div className="w-[127px] h-[127px] rounded-full border-[3px] border-white shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1)] overflow-hidden">
                            <img
                                src={profileImageSrc}
                                alt="Profile"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        {/* Online dot */}
                        <div className="absolute bottom-1 right-1 w-5 h-5 rounded-full bg-[#19cf7a] border-[2px] border-white" />
                    </div>

                    {/* Hashtag pills */}
                    {(genres.length > 0 || authors.mbti) && (
                        <div className="flex flex-row flex-wrap justify-center gap-1.5 mt-6">
                            {authors.mbti && (
                                <span className="bg-[rgba(25,207,122,0.1)] text-[#00c896] text-sm font-semibold rounded-3 px-3 py-1.5">{authors.mbti}</span>
                            )}
                            {genres.map((g, i) => (
                                <span key={i}className="bg-[#F3F4F6] text-[#4A5565] text-sm font-semibold rounded-3 px-3 py-1.5">#{g}</span>
                            ))}
                        </div>
                    )}



                    <div className={"flex flex-col justify-center gap-4"}>
                        <motion.h2 
                            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
                            className="text-[#101828] text-[26px] font-bold mt-4 text-center">
                            {authors.authorName}
                        </motion.h2>

                        {authors.authorIntro && (
                            <motion.span 
                                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
                                className="mx-3 text-sm text-[#4a5565] leading-relaxed">
                                {authors.authorIntro}
                            </motion.span>
                        )}
                    </div>




                    {/* SNS icons */}
                    <div className="flex flex-row items-center gap-3 mt-4">
                        {authors.instagramLink && (
                            <a href={authors.instagramLink} target="_blank" rel="noopener noreferrer"
                               className="group w-11 h-11 rounded-full border border-[#e5e7eb] flex items-center justify-center bg-white shadow-sm hover:!border-[#19cf7a] hover:-translate-y-1 hover:scale-108 hover:shadow-[0_0_15px_rgba(25,207,122,0.25)] transition-all duration-300 ease-out cursor-pointer [&_path]:transition-colors [&_path]:duration-300 [&_path]:group-hover:stroke-[#19cf7a]">
                                <Instar />
                            </a>
                        )}
                        {authors.xlink && (
                            <a href={authors.xlink} target="_blank" rel="noopener noreferrer"
                               className="group w-11 h-11 rounded-full border border-[#e5e7eb] flex items-center justify-center bg-white shadow-sm hover:!border-[#19cf7a] hover:-translate-y-1 hover:scale-108 hover:shadow-[0_0_15px_rgba(25,207,122,0.25)] transition-all duration-300 ease-out cursor-pointer [&_path]:transition-colors [&_path]:duration-300 [&_path]:group-hover:stroke-[#19cf7a]">
                                <Twitter />
                            </a>
                        )}
                        {(authors.brunchLink || authors.snsLink1 || authors.snsLink2 || authors.naverLink) && (
                            <button onClick={() => setShowAlert(true)}
                                    className="group w-11 h-11 rounded-full border border-[#e5e7eb] flex items-center justify-center bg-white shadow-sm hover:!border-[#19cf7a] hover:-translate-y-1 hover:scale-108 hover:shadow-[0_0_15px_rgba(25,207,122,0.25)] transition-all duration-300 ease-out cursor-pointer [&_path]:transition-colors [&_path]:duration-300 [&_path]:group-hover:stroke-[#19cf7a]">
                                <Link />
                            </button>
                        )}
                    </div>




                    {/* Follow button */}
                    {!authorYn && (
                        <button
                            onClick={(e) => {
                                // 로그인 상태 + 아직 팔로우 안 한 경우에만 폭죽
                                if (!isFollowing && authorStatus) {
                                    triggerConfetti(e, { count: 32, spread: 140 });
                                }
                                onFollowToggle();
                            }}
                            className={`mt-4 w-full h-[52px] flex items-center justify-center gap-2 text-base font-bold rounded-2xl text-white shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1)] transition-all active:scale-95 ${
                                isFollowing
                                    ? "bg-[#01863B] hover:bg-[#016b30]"
                                    : "bg-[#19cf7a] hover:bg-[#00b368]"
                            }`}
                        >
                            {!isFollowing && <Follow />}
                            {isFollowing ? "팔로우 중 ✓" : "팔로우"}
                        </button>
                    )}
                </div>
            </div>


            {/* 출간작 개수 stats card */}
            {bookCount !== undefined && (
                <div className="bg-white rounded-2xl border border-[#f3f4f6] shadow-sm p-6 flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-[rgba(25,207,122,0.1)] rounded-[24px] flex items-center justify-center">
                                <Book />
                            </div>
                            <span className="text-black text-[15px] font-medium">출간작 개수</span>
                        </div>
                        <span className="text-[#101828] text-2xl font-bold">{bookCount}</span>
                    </div>


                </div>
            )}



            {showAlert && (
                <SnsModal
                    title="SNS 링크"
                    onClose={() => setShowAlert(false)}
                    brunchLink={authors.brunchLink}
                    snsLink1={authors.snsLink1}
                    snsLink2={authors.snsLink2}
                    naverLink={authors.naverLink}
                />
            )}
            {followAlert && (
                <Alert
                    title="로그인이 필요해요"
                    message={"작가를 팔로우하려면\n로그인이 필요합니다."}
                    onClose={() => setFollowAlert(false)}
                    showLoginOnConfirm={true}
                />
            )}
        </div>
    );
};

export default AuthorDetailPage;
